import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const TICKETS_PER_PAGE = 4;

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [type, setType] = useState('');
  const [price, setPrice] = useState('');
  const [features, setFeatures] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [id, setId] = useState('');
  const [editType, setEditType] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editFeatures, setEditFeatures] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getTickets = async () => {
    const res = await supabase.from('tickets').select('*').order('id', { ascending: false });
    setTickets(res.data || []);
  };

  useEffect(() => {
    getTickets();
  }, []);

  const createTicket = async (e) => {
    e.preventDefault();
    await supabase.from('tickets').insert([{ type, price, features: features.split(',').map((item) => item.trim()) }]);
    setType('');
    setPrice('');
    setFeatures('');
    setIsCreateOpen(false);
    await getTickets();
  };

  const editTicket = (ticket) => {
    setId(ticket.id);
    setEditType(ticket.type || '');
    setEditPrice(ticket.price || '');
    setEditFeatures(Array.isArray(ticket.features) ? ticket.features.join(', ') : '');
    setIsEditOpen(true);
  };

  const updateTicket = async (e) => {
    e.preventDefault();
    await supabase
      .from('tickets')
      .update({ type: editType, price: editPrice, features: editFeatures.split(',').map((item) => item.trim()) })
      .eq('id', id);
    setIsEditOpen(false);
    await getTickets();
  };

  const deleteTicket = async (ticketId) => {
    await supabase.from('tickets').delete().eq('id', ticketId);
    await getTickets();
  };

  const filteredTickets = tickets.filter((ticket) => {
    const value = search.toLowerCase();
    return ticket.id.toString().includes(value) || ticket.type?.toLowerCase().includes(value);
  });

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / TICKETS_PER_PAGE));
  const startIndex = (currentPage - 1) * TICKETS_PER_PAGE;
  const paginatedTickets = filteredTickets.slice(startIndex, startIndex + TICKETS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage tickets" title="Tickets" />
        <div className="table_controls_card">
          <label className="table_search">
            <img alt="" src={searchIcon} />
            <input onChange={(e) => setSearch(e.target.value)} placeholder="Search tickets..." value={search} />
          </label>
          <div className="table_controls_actions">
            <span>{filteredTickets.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Ticket
            </button>
          </div>
        </div>
        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead>
                <tr><th>ID</th><th>Type</th><th>Price</th><th>Features</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {paginatedTickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{ticket.id}</td>
                    <td>{ticket.type}</td>
                    <td>{ticket.price}</td>
                    <td>{Array.isArray(ticket.features) ? ticket.features.join(', ') : ''}</td>
                    <td>
                      <div className="table_actions">
                        <button
                          aria-label={`Edit ticket #${ticket.id}`}
                          className="table_icon_button"
                          onClick={() => editTicket(ticket)}
                          type="button"
                        >
                          <img alt="" src={editIcon} />
                        </button>
                        <button
                          aria-label={`Delete ticket #${ticket.id}`}
                          className="table_icon_button table_icon_button_danger"
                          onClick={() => deleteTicket(ticket.id)}
                          type="button"
                        >
                          <img alt="" src={deleteIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">Next</button>
          </div>
        </div>
        {isCreateOpen && (
          <div className="table_modal_overlay" onClick={() => setIsCreateOpen(false)}>
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2>Add Ticket</h2>
                </div>
                <button className="table_modal_close" onClick={() => setIsCreateOpen(false)} type="button">
                  x
                </button>
              </div>
              <form className="table_form" onSubmit={createTicket}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Type</span>
                    <input onChange={(e) => setType(e.target.value)} placeholder="Type" value={type} />
                  </label>
                  <label className="table_form_field">
                    <span>Price</span>
                    <input onChange={(e) => setPrice(e.target.value)} placeholder="Price" value={price} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Features</span>
                    <input
                      onChange={(e) => setFeatures(e.target.value)}
                      placeholder="Features separated by comma"
                      value={features}
                    />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isEditOpen && (
          <div className="table_modal_overlay" onClick={() => setIsEditOpen(false)}>
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2>Edit Ticket</h2>
                </div>
                <button className="table_modal_close" onClick={() => setIsEditOpen(false)} type="button">
                  x
                </button>
              </div>
              <form className="table_form" onSubmit={updateTicket}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Type</span>
                    <input onChange={(e) => setEditType(e.target.value)} value={editType} />
                  </label>
                  <label className="table_form_field">
                    <span>Price</span>
                    <input onChange={(e) => setEditPrice(e.target.value)} value={editPrice} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Features</span>
                    <input onChange={(e) => setEditFeatures(e.target.value)} value={editFeatures} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tickets;
